import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config/apiBaseUrl';
import { CloseIcon, AlertTriangleIcon } from './Icons';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import './StockSearchModal.css';

export interface StockSearchResult {
  stockName: string;
  stockCode: string;
  ticker: string;
  exchange: string;
  country: string;
  countryFlag: string;
  market: string;
}

interface StockSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (stock: StockSearchResult) => void;
  initialQuery?: string;
}

const StockSearchModal: React.FC<StockSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialQuery = ''
}) => {
  const { modalRef } = useModalAccessibility(isOpen, onClose);
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<StockSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [market, setMarket] = useState<'ALL' | 'KR' | 'US' | 'JP' | 'EU'>('ALL');

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const firstResultRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // 모달 열릴 때 포커스 & 초기 쿼리 설정
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setResults([]);
      setError(null);
      setSelectedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialQuery]);

  // 검색 실행 (Java 백엔드 DB 기반)
  const doSearch = useCallback(async (searchQuery: string, searchMarket: string) => {
    if (!searchQuery || searchQuery.trim().length < 1) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        query: searchQuery.trim(),
        market: searchMarket,
        limit: '30'
      });

      const response = await fetch(`${API_BASE_URL}/stock-data/stocks/search?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`검색 실패 (${response.status})`);
      }

      const data = await response.json();
      // Java API 응답: { success, data: { query, market, count, results }, message }
      const searchResults = data.data?.results || data.results || [];
      
      // DB 결과에 countryFlag 추가 (텍스트로 표시)
      const enrichedResults = searchResults.map((stock: any) => {
        let flag = 'KR';
        if (stock.country === 'US') flag = 'US';
        else if (stock.country === 'JP') flag = 'JP';
        else if (stock.countryFlag) {
          // 이모지가 있으면 그대로 사용 (Python 서비스에서 온 경우)
          flag = stock.countryFlag;
        } else if (['GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'CH', 'BE', 'AT', 'SE', 'NO', 'DK', 'FI', 'IE', 'PT'].includes(stock.country)) {
          flag = 'EU';
        }
        
        return {
          ...stock,
          exchange: stock.market || stock.exchange || '',
          countryFlag: flag,
        };
      });
      
      setResults(enrichedResults);
      setSelectedIndex(-1);
    } catch (err: any) {
      console.error('종목 검색 오류:', err);
      setError(err.message || '검색 중 오류가 발생했습니다.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 디바운스 검색
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (query.trim().length >= 1) {
      debounceTimer.current = setTimeout(() => {
        doSearch(query, market);
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, market, doSearch]);

  // 키보드 네비게이션
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < results.length) {
        handleSelect(results[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  // 선택 항목이 보이도록 스크롤
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.stock-search-item');
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleSelect = (stock: StockSearchResult) => {
    onSelect(stock);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="stock-search-overlay" onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        className="stock-search-modal modal-animated"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-search-title"
        onKeyDown={handleKeyDown}
      >
        {/* 헤더 */}
        <div className="stock-search-header">
          <h2 id="stock-search-title" className="sr-only">종목 검색</h2>
          <button className="stock-search-back-btn" onClick={onClose}>
            ←
          </button>
          <div className="stock-search-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="stock-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="종목명 또는 종목코드 검색"
              autoComplete="off"
            />
            {query && (
              <button
                className="stock-search-clear-btn"
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              >
                <CloseIcon size={14} color="currentColor" />
              </button>
            )}
          </div>
        </div>

        {/* 시장 필터 */}
        <div className="stock-search-market-filter">
          {(['ALL', 'KR', 'US', 'JP', 'EU'] as const).map((m, index) => (
            <button
              key={m}
              className={`market-filter-btn ${market === m ? 'active' : ''}`}
              onClick={() => setMarket(m)}
              onKeyDown={(e) => {
                // 마지막 필터 버튼에서 Tab을 누르면 첫 번째 검색 결과로 포커스
                if (e.key === 'Tab' && index === 4 && results.length > 0 && !e.shiftKey) {
                  e.preventDefault();
                  firstResultRef.current?.focus();
                }
              }}
            >
              {m === 'ALL' ? '전체' : m === 'KR' ? '한국' : m === 'US' ? '미국' : m === 'JP' ? '일본' : '유럽'}
            </button>
          ))}
        </div>

        {/* 결과 목록 */}
        <div className="stock-search-results" ref={listRef}>
          {isLoading && (
            <div className="stock-search-loading">
              <div className="loading-spinner" />
              <span>검색 중...</span>
            </div>
          )}

          {error && (
            <div className="stock-search-error">
              <span><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><AlertTriangleIcon size={16} color="currentColor" /></span> {error}</span>
              <small>서버가 실행 중인지 확인하세요.</small>
            </div>
          )}

          {!isLoading && !error && results.length === 0 && query.trim().length >= 1 && (
            <div className="stock-search-empty">
              <span>검색 결과가 없습니다</span>
              <small>다른 종목명이나 종목코드로 검색해보세요</small>
            </div>
          )}

          {!isLoading && !error && query.trim().length < 1 && (
            <div className="stock-search-empty">
              <span>종목명 또는 종목코드를 입력하세요</span>
              <small>예: 삼성전자, AAPL, 005930</small>
            </div>
          )}

          {results.map((stock, index) => (
            <div
              key={`${stock.stockCode}-${index}`}
              ref={index === 0 ? firstResultRef : null}
              tabIndex={0}
              className={`stock-search-item ${selectedIndex === index ? 'selected' : ''}`}
              onClick={() => handleSelect(stock)}
              onMouseEnter={() => setSelectedIndex(index)}
              onFocus={() => setSelectedIndex(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(stock);
                }
              }}
            >
              <div className="stock-search-item-main">
                <span className="stock-item-name">{stock.stockName}</span>
                <span className="stock-item-code">{stock.stockCode}</span>
              </div>
              <div className="stock-search-item-sub">
                <span className="stock-item-flag">{stock.countryFlag}</span>
                <span className="stock-item-market">{stock.market}</span>
              </div>
              <div className="stock-item-star">☆</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StockSearchModal;
